import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { isMobileDevice } from '../utils/deviceDetection';
import { Shell } from '../components/Shell';
import { DesktopRequired } from '../components/DesktopRequired';
import { KeyboardIcon } from '../components/Icons';

const CodeEntry: React.FC = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Initialize with detection result so first render is correct
  const [isMobile] = useState(() => isMobileDevice());
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (inputRef.current && !isMobile) {
      inputRef.current.focus();
    }
  }, [isMobile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (code.length !== 6) {
      setError('Please enter a 6-character code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/auth/verify-code', { code: code.toUpperCase() });

      if (response.data.success) {
        sessionStorage.setItem('userCode', response.data.code);
        sessionStorage.setItem('userToken', response.data.token);
        sessionStorage.removeItem('testResults');
        // Coming back part-way through: show their tries so far, with the option to take the next one
        navigate(response.data.tries_taken > 0 ? `/results/${response.data.code}` : `/test/${response.data.code}`);
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').substring(0, 6).toUpperCase();
    setCode(value);
    if (error) setError('');
  };

  const complete = code.length === 6;

  return (
    <Shell header={false}>
      <div className="entry-brand">
        <img src="/logo.svg" alt="" />
        <h1>TypeRV</h1>
      </div>

      <div className="card entry-card">
        {isMobile ? (
          <DesktopRequired />
        ) : (
          <>
            <form onSubmit={handleSubmit}>
              <label className="field-label" htmlFor="test-code">
                Enter Test Code
              </label>
              <input
                id="test-code"
                ref={inputRef}
                type="text"
                value={code}
                onChange={handleChange}
                placeholder="XXXXXX"
                maxLength={6}
                disabled={loading}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                className={`code-input${complete ? ' complete' : ''}`}
              />

              {/* Code length indicator */}
              <div className="code-dots" aria-hidden="true">
                {Array.from({ length: 6 }).map((_, i) => (
                  <span key={i} className={i < code.length ? 'filled' : ''} />
                ))}
              </div>

              {error && <div className="entry-error">{error}</div>}

              <button type="submit" disabled={loading || !complete} className="btn btn-primary btn-block btn-lg" style={{ marginTop: 20 }}>
                {loading ? <span className="loading" style={{ width: 16, height: 16 }} /> : 'Begin Test'}
              </button>
            </form>

            <div className="entry-divider">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={complete}
                onClick={() => navigate('/practice')}
              >
                <KeyboardIcon size={16} />
                Practice
              </button>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
};

export default CodeEntry;
